<script setup lang="ts">
import type { Component } from "vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import { errorToString } from "@/error/errorUnwrapper";
import { useUserStore } from "@/store/user";
import { firstQueryParam } from "@/utils/vue/queryParam";

import ChooseButton from "./ChooseButton.vue";
import { login } from "./LoginPage.utils";
import LoginPageEmail from "./LoginPageEmail.vue";

const emit = defineEmits<{ pageSwitch: [page: Component] }>();
const error = ref("");

const loginData = computed(() => useUserStore().loginData);
const { t } = useI18n();
const route = useRoute();

async function loginWithTenant(tenantId: string) {
  error.value = "";
  try {
    await login({
      email: loginData.value.email,
      type: firstQueryParam(route.query, "type") || "",
      path: firstQueryParam(route.query, "path") || "",
      tenantId,
    });
  } catch (err) {
    error.value = errorToString(err, t("loginPage.notLogin"));
  }
}

function switchPageToEmail() {
  emit("pageSwitch", LoginPageEmail);
}
</script>

<template>
  <div v-autofocus tabindex="-1" aria-labelledby="tenants-page-title">
    <h1 id="tenants-page-title" class="login">
      {{ $t("loginPageTenants.selectOrganization") }}
    </h1>
    <p class="description">
      {{ $t("loginPageTenants.selectOrganizationsAssociated", { email: loginData.email }) }}
      <BaseButton variant="text" @click="switchPageToEmail">
        {{ $t("loginPageTenants.changeEmail") }}
      </BaseButton>
    </p>
    <div class="list scrollable">
      <ChooseButton
        v-for="tenant in loginData.tenants"
        :key="tenant.id"
        size="small"
        @click="loginWithTenant(tenant.id)"
      >
        {{ tenant.name }}
      </ChooseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/font";
@use "@/styles/variables";
@use "@/styles/variables/colors";

.login {
  @include typography.extra-large;

  margin-bottom: 10px;
}

.description {
  @include typography.medium;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  color: colors.$text-subtle;
  margin-bottom: 30px;
  width: 380px;

  :deep(.base-button) {
    @include typography.medium("semi-bold");
  }
}

.list {
  max-height: 320px;
  overflow: hidden scroll;
  width: 380px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  :deep(.choose-button) {
    @include typography.medium("bold");

    height: 56px;
  }
}

.list::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: colors.$background-ui-inverse;
}

.scrollable {
  position: relative;
}
</style>
